import React, { useState } from 'react';
import { Alert, Button, Card, Image, message, Space } from 'antd';
import { useTranslation } from '@refinedev/core';
import {
  AddSectionModal,
  AssignRequestModal,
  CommentModal,
  ReviewPostModal,
  EditSectionModal,
} from '@/components/modals';
import { getFileUrl } from '@/Helpers';
import {
  EyeOutlined,
  PullRequestOutlined,
  CheckOutlined,
  EditOutlined,
  PlusOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { ListButton, RefreshButton, Show } from '@refinedev/antd';
import { ChangeRequestModal } from '@/components/modals/ChangeRequestModal';
import { ChangeRequestBadge } from '@/components/badges/ChangeRequestBadge';
import { endPoints } from '@/api/configs';
import useAPI, { useGet } from '@/api/useApi';
import { useParams } from 'react-router-dom';
import { PostReq } from '@/api/types/getOnePost';
import { SectionCard } from '@/components/sections/SectionCard';
import { PostInfoCard } from '@/components/posts/PostInfoCard';
import { useProfileStore } from '@/core/useUserProfile';
import { EditPostModal } from '@/components/modals/EditPostModal';
import {
  canAssign,
  canEditDuringReview,
  canManageChangeRequests,
  canReviewPostSubmission,
  getRequestErrorMessage,
  isReviewableStatus,
} from '@/utils/requestPermissions';

export const ShowPostReq: React.FC = () => {
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;
  const { id, reqId } = useParams();
  const [bannerError, setBannerError] = useState(false);
  const [addNewSectionModalVisible, setAddNewSectionModalVisible] =
    useState(false);
  const [reviewPostModalVisible, setReviewPostModalVisible] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const { translate } = useTranslation();
  const { usePost } = useAPI();
  const { data, isLoading, isError, error, refetch } = useGet<PostReq>(
    endPoints.post(id),
    endPoints.post(id)
  );
  const postReq = data?.data?.data;
  const isForbidden =
    isError && (error as { statusCode?: number })?.statusCode === 403;
  const requestStatus = postReq?.requests?.status;
  const canReview = canReviewPostSubmission(
    adminType,
    requestStatus,
    postReq?.requests?.stage
  );
  const allowChangeRequests = canManageChangeRequests(adminType);
  const allowEditDuringReview = canEditDuringReview(adminType);
  const showAssign =
    !!postReq?.requests && canAssign(postReq.requests, adminType);
  const { mutateAsync: createChangeRequest, isLoading: isLoadingChangeReq } =
    usePost(endPoints.requestChanges(reqId));

  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<
    number | undefined
  >(undefined);
  const [postChangeRequestVisible, setPostChangeRequestVisible] =
    useState(false);
  const [sectionChangeRequestVisible, setSectionChangeRequestVisible] =
    useState(false);
  const [selectedSectionRequests, setSelectedSectionRequests] = useState<any[]>(
    []
  );
  const [editSectionModalVisible, setEditSectionModalVisible] = useState(false);
  const [selectedSection, setSelectedSection] = useState<any>(null);

  const addCommentId = (sectionId?: number) => {
    if (sectionId) {
      setSelectedSectionId(sectionId);
    }
    setCommentModalVisible(true);
  };
  const handleAddComment = (comment: string) => {
    if (selectedSectionId) {
      createChangeRequest(
        {
          change_request: [
            {
              section_id: selectedSectionId,
              description: comment,
            },
          ],
        },
        {
          onSuccess: () => {
            setSelectedSectionId(undefined);
            setCommentModalVisible(false);
            message.success(translate('listing-request.success.add-comment'));
            refetch();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    } else {
      createChangeRequest(
        {
          change_request: [
            {
              description: comment,
            },
          ],
        },
        {
          onSuccess: () => {
            setSelectedSectionId(undefined);
            setCommentModalVisible(false);
            message.success(translate('listing-request.success.add-comment'));
            if (selectedSectionId) {
              setSectionChangeRequestVisible(false);
            }
            refetch();
          },
          onError: (error) => {
            message.error(error.message);
          },
        }
      );
    }
  };
  const { mutate: resolveChangeRequests, isLoading: resolveLoading } = usePost(
    `request/resolve-change-requests/${reqId}`
  );
  const resolveChangeRequestsHandler = (change_request_id?: number) => {
    resolveChangeRequests(
      {
        ...(change_request_id && {
          change_request_id,
        }),
      },
      {
        onSuccess: () => {
          message.success(translate('listing-request.success.resolve-comment'));
          setPostChangeRequestVisible(false);
          setSectionChangeRequestVisible(false);
          refetch();
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };
  const handleOpenModal = () => {
    setAddNewSectionModalVisible(true);
  };

  const handleEditSection = (section: any) => {
    setSelectedSection(section);
    setEditSectionModalVisible(true);
  };

  const hasPendingChangeRequests =
    (postReq?.change_requests?.filter((cr) => cr.status === 'PENDING')
      ?.length ?? 0) > 0 ||
    postReq?.sections?.some(
      (section) =>
        section.change_requests?.filter((cr) => cr.status === 'PENDING')
          ?.length > 0
    );

  return (
    <Show
      title={
        <div className="flex gap-x-3 items-center">
          <PullRequestOutlined />
          <h1>{translate('listing-request.title')}</h1>
          {allowChangeRequests && (
            <ChangeRequestBadge
              changeRequests={[]}
              onClick={() => setPostChangeRequestVisible(true)}
            />
          )}
        </div>
      }
      isLoading={isLoading}
      headerButtons={({ listButtonProps }) => (
        <>
          {listButtonProps && !isForbidden && (
            <Space>
              <ListButton {...listButtonProps}>
                {translate('buttons.list')}
              </ListButton>
              <RefreshButton />

              {showAssign && (
                <Button
                  onClick={() => setAssignModalOpen(true)}
                  icon={<UserAddOutlined />}
                  type="default"
                >
                  {translate('listing-request.assign.button')}
                </Button>
              )}

              {allowEditDuringReview &&
                adminType &&
                isReviewableStatus(requestStatus) && (
                  <Button
                    onClick={() => setIsEditModalOpen(true)}
                    icon={<EditOutlined />}
                    type="default"
                  >
                    {translate('buttons.edit')}
                  </Button>
                )}

              {canReview && (
                <Button
                  onClick={() => setReviewPostModalVisible(true)}
                  icon={<EyeOutlined />}
                  type="primary"
                >
                  {translate('buttons.review')}
                </Button>
              )}
              {allowChangeRequests && hasPendingChangeRequests && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={resolveLoading}
                  onClick={() => resolveChangeRequestsHandler()}
                >
                  {translate('buttons.resolveAllComments')}
                </Button>
              )}
              {allowEditDuringReview && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleOpenModal}
                >
                  {translate('section.add-section')}
                </Button>
              )}
            </Space>
          )}
        </>
      )}
    >
      {isForbidden ? (
        <Alert
          type="error"
          showIcon
          message={getRequestErrorMessage(error, translate)}
        />
      ) : (
        <>
          <PostInfoCard
            postReq={postReq}
            onAddComment={addCommentId}
            isLoading={isLoading}
            onChangeRequest={(changeRequests) => {
              setSelectedSectionRequests(changeRequests);
              setSectionChangeRequestVisible(true);
            }}
            showChangeRequestActions={allowChangeRequests}
          />

          <Card className="mb-6">
            {postReq?.banner &&
              (!bannerError ? (
                <div>
                  <Image
                    src={getFileUrl(postReq.banner.id.toString())}
                    alt={postReq.banner.file_name}
                    className="w-full object-cover rounded-lg"
                    preview={{
                      mask: <div className="text-lg">Click to view</div>,
                    }}
                    onError={() => {
                      setBannerError(true);
                    }}
                  />
                  {postReq.banner.description && (
                    <div className="text-sm text-gray-500">
                      {postReq.banner.description}
                    </div>
                  )}
                </div>
              ) : (
                <Alert
                  message={translate('messages.missing-media')}
                  type="error"
                />
              ))}
          </Card>

          {postReq?.sections?.map((section) => (
            <SectionCard
              key={section.id}
              refetch={refetch}
              reqStatus={postReq?.requests?.status}
              section={section}
              onAddComment={addCommentId}
              onChangeRequest={(changeRequests) => {
                setSelectedSectionRequests(changeRequests);
                setSectionChangeRequestVisible(true);
              }}
              onEdit={allowEditDuringReview ? handleEditSection : undefined}
              readOnly={!allowEditDuringReview}
            />
          ))}

          {allowChangeRequests && commentModalVisible && (
            <CommentModal
              open={commentModalVisible}
              onClose={() => setCommentModalVisible(false)}
              sectionId={selectedSectionId}
              isLoading={isLoadingChangeReq}
              handleAddComment={handleAddComment}
            />
          )}
          {allowChangeRequests && postChangeRequestVisible && (
            <ChangeRequestModal
              open={postChangeRequestVisible}
              onClose={() => setPostChangeRequestVisible(false)}
              changeRequests={selectedSectionRequests}
              title={translate('changeRequests.post.title')}
              onResolve={resolveChangeRequestsHandler}
              resolveLoading={resolveLoading}
            />
          )}
          {allowChangeRequests && sectionChangeRequestVisible && (
            <ChangeRequestModal
              open={sectionChangeRequestVisible}
              onClose={() => setSectionChangeRequestVisible(false)}
              changeRequests={selectedSectionRequests}
              title={translate('changeRequests.section.title')}
              onResolve={resolveChangeRequestsHandler}
              resolveLoading={resolveLoading}
            />
          )}
          {reviewPostModalVisible && (
            <ReviewPostModal
              open={reviewPostModalVisible}
              reqId={reqId}
              onClose={() => setReviewPostModalVisible(false)}
              refetch={refetch}
            />
          )}
          {showAssign && postReq && (
            <AssignRequestModal
              open={assignModalOpen}
              request={{ id: postReq.requests.id, post_title: postReq.title }}
              onClose={() => setAssignModalOpen(false)}
              refetch={refetch}
            />
          )}
          {allowEditDuringReview && postReq && (
            <EditPostModal
              open={isEditModalOpen}
              onCancel={() => setIsEditModalOpen(false)}
              refetch={refetch}
              postId={id || ''}
              initialValues={{
                title: postReq.title || '',
                view_period: postReq.view_period || '',
                location: postReq.location || '',
                type: postReq.type || '',
                date: postReq.date || '',
                summary: postReq.summary || '',
                topics: postReq.topics?.map((topic) => topic.id) || [],
                banner_id: postReq.banner_id || postReq.banner?.id,
                main_audio_id:
                  postReq.main_audio_id || postReq.main_audio?.id || 0,
                main_video_id:
                  postReq.main_video_id || postReq.main_video?.id || 0,
              }}
            />
          )}
          {allowEditDuringReview && addNewSectionModalVisible && (
            <AddSectionModal
              open={addNewSectionModalVisible}
              onCancel={() => setAddNewSectionModalVisible(false)}
              refetch={refetch}
              postId={id || ''}
            />
          )}
          {allowEditDuringReview &&
            editSectionModalVisible &&
            selectedSection && (
              <EditSectionModal
                open={editSectionModalVisible}
                onCancel={() => {
                  setEditSectionModalVisible(false);
                  setSelectedSection(null);
                }}
                refetch={refetch}
                postId={id || ''}
                sectionId={selectedSection.id.toString()}
                initialValues={{
                  type: selectedSection.type,
                  view: selectedSection.view,
                  title: selectedSection.title,
                  content: selectedSection.content,
                  quote: selectedSection.quote,
                  description: selectedSection.description,
                  uploads: selectedSection.uploads,
                }}
              />
            )}
        </>
      )}
    </Show>
  );
};
